<template>
  <div ref="elementRef">
    <div v-style:bg="backgroundColor" class="relative grid">
      <div class="relative flex grid-area-stack">
        <cms-base-caption v-if="hasCaption" v-bind="{ captionColor, captionStyle, captionPosition, captionText }" />
        <base-picture
          v-if="responsiveMedia.images"
          v-bind="responsiveMedia.images"
          class="w-full"
          fit="cover"
          :lazy
        />
        <vf-video
          v-else-if="responsiveMedia.video"
          v-bind="responsiveMedia.video"
          ref="videoRef"
          :autoplay="!lazy"
          class="w-full"
          fit="cover"
          loop
          muted
        />
        <base-link
          v-if="mediaLink"
          aria-hidden="true"
          class="absolute-0"
          :data-cta="mediaLink.delocalizedTracking"
          tabindex="-1"
          :target="mediaLink.targetAttribute"
          :to="mediaLink.url"
          @click="handlePromotionClick('img')"
        />
      </div>
      <div
        :class="[...contentAlignment, brandClasses.contentContainer]"
        :style="{ gap: backdrop ? '4.5rem' : '3rem' }"
      >
        <div
          v-style:bg="backdrop"
          :class="[
            alignmentSmall.includes('overlay') ? 'p-2 -mx-2' : '<md:bg-transparent',
            brandClasses.backdropContainer,
          ]"
        >
          <div v-if="eyebrowText" v-style:c="eyebrowTextColor || subtitleColor" class="uppercase">
            {{ eyebrowText }}
          </div>
          <base-picture
            v-if="images"
            v-bind="images"
            class="max-h-38"
            fit="contain"
            :props-img="{ class: 'object-left' }"
          />
          <base-picture
            v-else-if="eyebrowLogo"
            :alt="eyebrowLogo.alt"
            class="max-h-38"
            fit="contain"
            :props-img="{ class: 'object-left' }"
            :src="eyebrowLogo.url"
          />
          <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
            {{ overline }}
          </p>
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </base-link>
            <template v-else>
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </template>
          </component>
          <p
            v-if="subtitle"
            v-style:c="subtitleColor"
            :class="subtitleStyle"
            data-test-id="cms-banner-text"
          >
            <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
            <span v-else>{{ subtitle }}</span>
          </p>
          <cms-rich-text v-if="richText" v-style:c="richTextColor || subtitleColor" class="pointer-within" :content="{ richText }" />
        </div>
        <div
          v-if="linkType !== 'No-CTA' && targets.length && !alignmentLarge.includes('center')"
          class="pointer-within gap-4 wrap <md:hidden lg:gap-6 "
          :class="ctaClass"
          :style="`--cols: repeat(${targets.length},minmax(0,1fr))`"
        >
          <cms-shared-button
            v-for="(target, i) in targets"
            :key="i"
            v-bind="{ ...target, ...getTextLinkStyles(content) }"
            @click="handlePromotionClick(`cta${i + 1}`)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="linkType !== 'No-CTA' && targets.length"
      class="f-col gap-4 px-4 pb-6 md:hidden!"
      :class="ctaClass"
      :style="`--cols: repeat(${targets.length},minmax(0,1fr))`"
    >
      <cms-shared-button v-for="(target, i) in targets" :key="i" v-bind="{ ...target, ...getTextLinkStyles(content) }" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type {
  BannerThinAlignmentLarge,
  BannerThinAlignmentSmall,
  BannerThinContent
} from '#types/components/cms/banner-thin'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{
  content: BannerThinContent
}>()

const { brandClasses } = useAppConfig().components.cms.bannerThin

const {
  alignmentLarge,
  alignmentSmall,
  backdrop,
  backgroundColor,
  captionColor,
  captionPosition,
  captionStyle,
  captionText,
  equalTargetSize,
  eyebrowLogo,
  eyebrowLogoResponsive,
  eyebrowText,
  eyebrowTextColor,
  hideTitle,
  linkType,
  media,
  mediaTarget,
  name,
  overline,
  overlineColor,
  overlineStyle,
  promotionTracking,
  richText,
  richTextColor,
  subtitle,
  subtitleHtml,
  subtitleColor,
  subtitleStyle,
  targets = [],
  title,
  titleHtml,
  titleColor,
  titleH1,
  titleStyle,
} = content

const { getMedia } = useCms()
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const videoRef = ref()
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getMedia(media)
const { images } = getMedia(eyebrowLogoResponsive)

const contentAlignmentLarge: Record<BannerThinAlignmentLarge, string> = {
  'left-right': 'md:between',
  'center-center': 'md:justify-center md:text-center'
}

const contentAlignmentSmall: Partial<Record<BannerThinAlignmentSmall, string>> = {
  'left-overlay': '<md:grid-area-stack <md:pointer-events-none <md:grid-area-stack',
  'center-overlay': '<md:pointer-events-none <md:justify-center <md:text-center <md:grid-area-stack',
  'center-below': '<md:justify-center <md:text-center'
}

const ctaAlignmentLarge: Record<BannerThinAlignmentLarge, string> = {
  'left-right': 'md:relative md:col-start-2 md:row-start-1 md:justify-end',
  'center-center': 'md:hidden'
}

const ctaAlignmentSmall: Record<BannerThinAlignmentSmall, string> = {
  'left-overlay': '<md:items-start <md:mt-6',
  'left-below': '<md:items-start',
  'center-overlay': '<md:items-center <md:text-center <md:mt-6',
  'center-below': '<md:items-center <md:text-center'
}

const hasCaption = captionText && captionPosition !== 'off'

const equalTargetSizeClass = [
  getValueForBreakpoint('sm', equalTargetSize) ? '<md:grid <md:cols-1 <md:auto-rows-fr' : '<md:flex',
  getValueForBreakpoint('md', equalTargetSize) ? '~md:grid ~md:cols-$cols ~md:auto-rows-fr ~md:items-stretch' : '~md:flex',
  getValueForBreakpoint('lg', equalTargetSize) ? 'lg:grid lg:cols-$cols lg:auto-rows-fr lg:items-stretch' : 'lg:flex',
].filter(Boolean).join(' ')

const contentAlignment = [
  contentAlignmentLarge[alignmentLarge],
  contentAlignmentSmall[alignmentSmall]
]

const ctaClass = [
  equalTargetSizeClass,
  ctaAlignmentSmall[alignmentSmall],
  ctaAlignmentLarge[alignmentLarge]
]

const titleTag = titleH1 ? 'h1' : 'h2'

onMounted(() => videoRef.value?.play().catch((e) => log.error(e)))
</script>
